import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import React from 'react';
import { TenderTypes } from '@web-kc/core/api';
import { CPagination } from '@web-kc/shared/ui';
import { useTranslation } from 'next-i18next';

const tableHeadRti = [
  { id: 1, label: 'plans:year', align: 'left' },
  { id: 2, label: 'plans:term', align: 'center' },
  { id: 4, label: 'plans:count', align: 'center' },
  { id: 6, label: 'plans:sum', align: 'right' },
];

const tableHeadKc = [
  { id: 0, label: 'plans:organization', align: 'left' },
  { id: 1, label: 'plans:year', align: 'left' },
  { id: 2, label: 'plans:term', align: 'center' },
  { id: 3, label: 'plans:form', align: 'left' },
  { id: 4, label: 'plans:count', align: 'center' },
  { id: 6, label: 'plans:sum', align: 'right' },
  { id: 7, label: 'plans:status', align: 'left' },
];

interface PlanListTableProps {
  data?: TenderTypes['PageTenderPlanDto'];
  isLoading: boolean;
  isRti: boolean;
  currentPage: number;
  onPageChange: (page: number) => void;
  onRowClick: (planId: number) => void;
}

export const PlanListTable: React.FC<PlanListTableProps> = ({
  data,
  isLoading,
  isRti,
  currentPage,
  onPageChange,
  onRowClick,
}) => {
  const { t } = useTranslation(['plans', 'common']);

  const sortedHeaders = (isRti ? tableHeadRti : tableHeadKc).sort(
    (a, b) => a.id - b.id,
  );

  return (
    <>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              {sortedHeaders.map(({ id, label, align }) => (
                <TableCell key={id} align={align as 'left'}>
                  {t(label)}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {data?.content?.map((plan, idx) => (
              <TableRow
                key={`row-${idx}-${plan?.id}`}
                onClick={() => onRowClick(Number(plan.id))}
                hover
              >
                {!isRti && (
                  <TableCell align="left">{plan?.organization}</TableCell>
                )}
                <TableCell align="left">{plan?.year}</TableCell>
                <TableCell align="center">{plan?.term?.name}</TableCell>
                {!isRti && (
                  <TableCell align="left">{plan?.form?.name}</TableCell>
                )}
                <TableCell align="center">{plan?.lots}</TableCell>
                <TableCell align="right">
                  {plan?.amount?.toLocaleString('ru-RU', {
                    maximumFractionDigits: 9,
                  })}
                </TableCell>
                {!isRti && (
                  <TableCell align="left">{plan?.status?.name}</TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {isLoading ? (
        <Box display="flex" height="120px">
          <CircularProgress sx={{ margin: 'auto' }} />
        </Box>
      ) : (
        !data?.totalElements && (
          <Typography textAlign="center" mt="20px">
            {t('common:common.emptyList')}
          </Typography>
        )
      )}

      {!!data?.totalElements && (
        <Box mt="20px">
          <Stack direction="row" alignItems="center" gap="20px">
            <Box mb={4}>
              <CPagination
                count={data?.totalPages ?? 0}
                currentPage={currentPage}
                handlePageChange={onPageChange}
              />
            </Box>
          </Stack>
        </Box>
      )}
    </>
  );
};

export default PlanListTable;
